import React, { useState } from 'react';
import { Plus, Trash2, Layers, Tag, DollarSign, Calendar, Info } from 'lucide-react';
import ImageUploadV2 from '../common/ImageUploadV2';

// Categories Tab Component
export const CategoriesTab = ({ categories, addCategory, updateCategory, deleteCategory }) => {
    return (
        <div className="space-y-4">
            <div className="flex items-center justify-between mb-6">
                <div>
                    <h3 className="text-lg font-semibold text-gray-900">Ticket Categories</h3>
                    <p className="text-sm text-gray-600 mt-1">
                        Organize tickets into categories (e.g., Student, Professional, VIP) - Optional
                    </p>
                </div>
                <button
                    onClick={addCategory}
                    className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                    <Plus className="w-5 h-5 mr-2" />
                    Add Category
                </button>
            </div>

            {categories.length === 0 ? (
                <div className="text-center py-12 bg-gray-50 rounded-xl border-2 border-dashed border-gray-300">
                    <Layers className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-600 mb-2">No categories defined</p>
                    <p className="text-sm text-gray-500 mb-4">
                        Categories are optional. Skip this if you don't need to organize tickets.
                    </p>
                    <button
                        onClick={addCategory}
                        className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                    >
                        <Plus className="w-5 h-5 mr-2" />
                        Add Category
                    </button>
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {categories.map((category) => (
                        <div key={category.id} className="bg-white border-2 border-gray-200 rounded-xl p-6 hover:border-purple-300 transition-all">
                            <div className="flex items-start justify-between mb-4">
                                <Layers className="w-8 h-8 text-purple-600" />
                                <button
                                    onClick={() => deleteCategory(category.id)}
                                    className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                >
                                    <Trash2 className="w-5 h-5" />
                                </button>
                            </div>

                            <div className="space-y-3">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Category Name
                                    </label>
                                    <input
                                        type="text"
                                        value={category.name}
                                        onChange={(e) => updateCategory(category.id, { name: e.target.value })}
                                        placeholder="e.g., Student, Professional"
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Description (Optional)
                                    </label>
                                    <textarea
                                        value={category.description || ''}
                                        onChange={(e) => updateCategory(category.id, { description: e.target.value })}
                                        placeholder="Describe this ticket category..."
                                        rows={2}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none"
                                    />
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 mt-6">
                <div className="flex">
                    <Info className="w-5 h-5 text-purple-600 mr-3 flex-shrink-0 mt-0.5" />
                    <div className="text-sm text-purple-800">
                        <strong>Note:</strong> Categories are optional and help organize tickets.
                        If you don't use categories, all tickets will be displayed directly under their region.
                    </div>
                </div>
            </div>
        </div>
    );
};

// Tickets Tab Component
export const TicketsTab = ({
    tickets,
    regions,
    categories,
    enablePriceByDate,
    addTicket,
    updateTicket,
    deleteTicket,
    expandedTicketId,
    setExpandedTicketId,
    moveTicket
}) => {
    return (
        <div className="space-y-4">
            <div className="flex items-center justify-between mb-6">
                <div>
                    <h3 className="text-lg font-semibold text-gray-900">Tickets</h3>
                    <p className="text-sm text-gray-600 mt-1">
                        Create and configure your event tickets with pricing and details
                    </p>
                </div>
                <button
                    onClick={addTicket}
                    disabled={regions.length === 0}
                    className="inline-flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                    <Plus className="w-5 h-5 mr-2" />
                    Add Ticket
                </button>
            </div>

            {regions.length === 0 && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4">
                    <div className="flex">
                        <Info className="w-5 h-5 text-amber-600 mr-3 flex-shrink-0 mt-0.5" />
                        <div className="text-sm text-amber-800">
                            <strong>Please add at least one region first</strong> before creating tickets.
                            Go to the "Regions" tab to get started.
                        </div>
                    </div>
                </div>
            )}

            {tickets.length === 0 ? (
                <div className="text-center py-12 bg-gray-50 rounded-xl border-2 border-dashed border-gray-300">
                    <Tag className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-600 mb-4">No tickets created yet</p>
                    <button
                        onClick={addTicket}
                        disabled={regions.length === 0}
                        className="inline-flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                        <Plus className="w-5 h-5 mr-2" />
                        Create Your First Ticket
                    </button>
                </div>
            ) : (
                <div className="space-y-4">
                    {tickets.map((ticket, index) => (
                        <TicketCard
                            key={ticket.id}
                            ticket={ticket}
                            index={index}
                            totalTickets={tickets.length}
                            regions={regions}
                            categories={categories}
                            enablePriceByDate={enablePriceByDate}
                            updateTicket={updateTicket}
                            deleteTicket={deleteTicket}
                            isExpanded={expandedTicketId === ticket.id}
                            onToggleExpand={() => setExpandedTicketId(expandedTicketId === ticket.id ? null : ticket.id)}
                            onMoveUp={() => moveTicket(ticket.id, 'up')}
                            onMoveDown={() => moveTicket(ticket.id, 'down')}
                        />
                    ))}
                </div>
            )}
        </div>
    );
};

// Ticket Card Component
const TicketCard = ({
    ticket,
    index,
    totalTickets,
    regions,
    categories,
    enablePriceByDate,
    updateTicket,
    deleteTicket,
    isExpanded,
    onToggleExpand,
    onMoveUp,
    onMoveDown
}) => {
    const [showPriceDateModal, setShowPriceDateModal] = useState(false);
    const [newPriceDate, setNewPriceDate] = useState({});
    const [showIdCardUpload, setShowIdCardUpload] = useState(false);

    const regionName = regions.find(r => r.id === ticket.regionType)?.name || 'Unknown';
    const categoryName = ticket.categoryId
        ? categories.find(c => c.id === ticket.categoryId)?.name
        : 'No Category';

    const handleIdCardUpload = (e) => {
        const file = e.target.files[0];
        if (file) {
            const reader = new FileReader();
            reader.onloadend = () => {
                updateTicket(ticket.id, { idCardBackground: reader.result });
            };
            reader.readAsDataURL(file);
        }
    };

    const addPriceDate = () => {
        if (!newPriceDate.startDate || !newPriceDate.endDate || !newPriceDate.price) return;

        const priceDate = {
            id: `price-${Date.now()}`,
            startDate: newPriceDate.startDate,
            endDate: newPriceDate.endDate,
            price: newPriceDate.price,
        };

        updateTicket(ticket.id, {
            priceByDate: [...(ticket.priceByDate || []), priceDate]
        });

        setNewPriceDate({});
        setShowPriceDateModal(false);
    };

    const removePriceDate = (priceId) => {
        updateTicket(ticket.id, {
            priceByDate: ticket.priceByDate?.filter(p => p.id !== priceId)
        });
    };

    return (
        <div className={`bg-white border-2 rounded-xl transition-all ${isExpanded ? 'border-green-500 shadow-md' : 'border-gray-200 hover:border-green-300'}`}>
            {/* Accordion Header */}
            <div
                className="flex items-center justify-between p-4 cursor-pointer"
                onClick={onToggleExpand}
            >
                <div className="flex items-center space-x-3">
                    <div className="flex flex-col space-y-1 mr-2">
                        <button
                            onClick={(e) => { e.stopPropagation(); onMoveUp(); }}
                            disabled={index === 0}
                            className="p-1 hover:bg-gray-100 rounded text-gray-400 hover:text-gray-600 disabled:opacity-30 disabled:cursor-not-allowed"
                        >
                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 15l7-7 7 7" /></svg>
                        </button>
                        <button
                            onClick={(e) => { e.stopPropagation(); onMoveDown(); }}
                            disabled={index === totalTickets - 1}
                            className="p-1 hover:bg-gray-100 rounded text-gray-400 hover:text-gray-600 disabled:opacity-30 disabled:cursor-not-allowed"
                        >
                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M19 9l-7 7-7-7" /></svg>
                        </button>
                    </div>

                    <Tag className={`w-8 h-8 ${ticket.isActive ? 'text-green-600' : 'text-gray-400'}`} />

                    <div>
                        <h4 className="text-lg font-bold text-gray-900">
                            {ticket.name || 'New Ticket'}
                        </h4>
                        <div className="flex items-center space-x-2 mt-1">
                            <span className="text-xs px-2 py-0.5 bg-blue-100 text-blue-700 rounded font-medium">
                                {regionName}
                            </span>
                            {ticket.ticketType === 'category' && (
                                <span className="text-xs px-2 py-0.5 bg-purple-100 text-purple-700 rounded font-medium">
                                    {ticket.categoryName || 'Category Ticket'}
                                </span>
                            )}
                            <span className="text-xs px-2 py-0.5 bg-gray-100 text-gray-700 rounded font-medium">
                                {ticket.price ? `${ticket.price}` : 'Free'}
                            </span>
                        </div>
                    </div>
                </div>

                <div className="flex items-center space-x-3">
                    <button
                        onClick={(e) => { e.stopPropagation(); updateTicket(ticket.id, { isActive: !ticket.isActive }); }}
                        className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${ticket.isActive
                            ? 'bg-green-100 text-green-700 hover:bg-green-200'
                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                            }`}
                    >
                        {ticket.isActive ? 'Active' : 'Hidden'}
                    </button>
                    <button
                        onClick={(e) => { e.stopPropagation(); deleteTicket(ticket.id); }}
                        className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                    >
                        <Trash2 className="w-5 h-5" />
                    </button>
                    <button className="p-1 text-gray-400">
                        <svg
                            className={`w-6 h-6 transform transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                        >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                    </button>
                </div>
            </div>

            {/* Accordion Content */}
            {isExpanded && (
                <div className="p-6 border-t border-gray-200 bg-gray-50 rounded-b-xl">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                        {/* Ticket Name */}
                        <div className="md:col-span-2">
                            <label className="block text-sm font-medium text-gray-700 mb-2">Ticket Name</label>
                            <input
                                type="text"
                                value={ticket.name}
                                onChange={(e) => updateTicket(ticket.id, { name: e.target.value })}
                                placeholder="e.g., General Admission"
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            />
                        </div>

                        {/* Ticket Type */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Ticket Type</label>
                            <select
                                value={ticket.ticketType || 'normal'}
                                onChange={(e) => updateTicket(ticket.id, { ticketType: e.target.value })}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            >
                                <option value="normal">Normal Ticket</option>
                                <option value="category">Category Ticket</option>
                            </select>
                            <p className="text-xs text-gray-500 mt-1">
                                {ticket.ticketType === 'category' ? 'Category tickets can be selected alongside normal tickets' : 'Standard ticket type'}
                            </p>
                        </div>

                        {/* Category Group Name - Only for Category Tickets */}
                        {ticket.ticketType === 'category' && (
                            <div className="md:col-span-2 bg-purple-50 p-3 rounded-lg border border-purple-200">
                                <label className="block text-sm font-medium text-purple-900 mb-2">
                                    Category Group Name <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="text"
                                    value={ticket.categoryName || ''}
                                    onChange={(e) => updateTicket(ticket.id, { categoryName: e.target.value })}
                                    placeholder="e.g., 'Pre-Workshop', 'Family Members'"
                                    className="w-full px-3 py-2 border border-purple-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                                />
                                <p className="text-xs text-purple-700 mt-1">
                                    Tickets with the same Category Group Name will be grouped together.
                                    Participants can select 1 ticket from this group.
                                </p>
                            </div>
                        )}

                        {/* Region */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Region</label>
                            <select
                                value={ticket.regionType}
                                onChange={(e) => updateTicket(ticket.id, { regionType: e.target.value })}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            >
                                {regions.map(region => (
                                    <option key={region.id} value={region.id}>{region.name}</option>
                                ))}
                            </select>
                        </div>

                        {/* Category (Optional) */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Category Tag (Optional)
                            </label>
                            <select
                                value={ticket.categoryId || ''}
                                onChange={(e) => updateTicket(ticket.id, { categoryId: e.target.value || undefined })}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            >
                                <option value="">No Category</option>
                                {categories.map(category => (
                                    <option key={category.id} value={category.id}>{category.name}</option>
                                ))}
                            </select>
                        </div>

                        {/* Price */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Price</label>
                            <div className="relative">
                                <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                                <input
                                    type="number"
                                    value={ticket.price === 0 ? '' : ticket.price}
                                    onChange={(e) => updateTicket(ticket.id, { price: e.target.value === '' ? 0 : parseFloat(e.target.value) })}
                                    className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    min="0"
                                    step="0.01"
                                    placeholder="0.00"
                                />
                            </div>
                        </div>

                        {/* Discount Price */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Discount Price (Optional)
                            </label>
                            <div className="relative">
                                <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                                <input
                                    type="number"
                                    value={ticket.discountPrice || ''}
                                    onChange={(e) => updateTicket(ticket.id, {
                                        discountPrice: e.target.value ? parseFloat(e.target.value) : undefined
                                    })}
                                    className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    min="0"
                                    step="0.01"
                                    placeholder="Optional"
                                />
                            </div>
                        </div>
                    </div>

                    {/* Sub Text */}
                    <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Sub Text (Short description)
                        </label>
                        <input
                            type="text"
                            value={ticket.subText || ''}
                            onChange={(e) => updateTicket(ticket.id, { subText: e.target.value })}
                            placeholder="e.g., Includes lunch and workshop materials"
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                    </div>

                    {/* Description */}
                    <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Description
                        </label>
                        <textarea
                            value={ticket.description || ''}
                            onChange={(e) => updateTicket(ticket.id, { description: e.target.value })}
                            placeholder="Add detailed information about this ticket (shown in info modal)..."
                            rows={3}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                        />
                    </div>

                    {/* More Info (deprecated but kept for backward compatibility) */}
                    {ticket.moreInfo && (
                        <div className="mb-4">
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                More Info (Legacy)
                            </label>
                            <textarea
                                value={ticket.moreInfo || ''}
                                onChange={(e) => updateTicket(ticket.id, { moreInfo: e.target.value })}
                                placeholder="Add detailed information about this ticket..."
                                rows={3}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                            />
                        </div>
                    )}

                    {/* ID Card Background - S3 Upload */}
                    <div className="mb-4">
                        <div className="flex items-center justify-between mb-2">
                            <label className="block text-sm font-medium text-gray-700">
                                ID Card Background (Optional)
                            </label>
                            <button
                                type="button"
                                onClick={() => setShowIdCardUpload(!showIdCardUpload)}
                                className="text-xs text-blue-600 hover:text-blue-700"
                            >
                                {showIdCardUpload ? 'Hide' : 'Add Custom Background'}
                            </button>
                        </div>

                        {showIdCardUpload && (
                            <div className="space-y-2">
                                <p className="text-xs text-gray-500 mb-2">
                                    Upload custom ID card background for this ticket type. If not provided, default event background will be used.
                                </p>
                                <ImageUploadV2
                                    onUploadSuccess={(url) => updateTicket(ticket.id, { idCardBackground: url })}
                                    onUploadError={(error) => console.error('Upload error:', error)}
                                    uploadEndpoint="/api/v2/events/upload/ticket-background"
                                    currentImageUrl={ticket.idCardBackground}
                                    label=""
                                />
                            </div>
                        )}
                    </div>

                    {/* Price by Date Section */}
                    {enablePriceByDate && (
                        <div className="border-t pt-4">
                            <div className="flex items-center justify-between mb-3">
                                <h4 className="font-semibold text-gray-900 flex items-center">
                                    <Calendar className="w-5 h-5 mr-2 text-blue-600" />
                                    Price by Date
                                </h4>
                                <button
                                    onClick={() => setShowPriceDateModal(true)}
                                    className="text-sm text-blue-600 hover:text-blue-700 font-medium"
                                >
                                    + Add Date Range
                                </button>
                            </div>

                            {ticket.priceByDate && ticket.priceByDate.length > 0 && (
                                <div className="space-y-2">
                                    {ticket.priceByDate.map(pd => (
                                        <div key={pd.id} className="flex items-center justify-between bg-gray-50 p-3 rounded-lg">
                                            <div className="flex-1">
                                                <p className="text-sm font-medium text-gray-900">
                                                    ${pd.price}
                                                </p>
                                                <p className="text-xs text-gray-600">
                                                    {new Date(pd.startDate).toLocaleDateString()} - {new Date(pd.endDate).toLocaleDateString()}
                                                </p>
                                            </div>
                                            <button
                                                onClick={() => removePriceDate(pd.id)}
                                                className="p-1 text-red-600 hover:bg-red-50 rounded"
                                            >
                                                <Trash2 className="w-4 h-4" />
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            )}

                            {/* Price Date Modal */}
                            {showPriceDateModal && (
                                <div className="mt-4 bg-blue-50 border-2 border-blue-200 rounded-lg p-4">
                                    <h5 className="font-semibold text-gray-900 mb-3">Add Price Date Range</h5>
                                    <div className="grid grid-cols-2 gap-3 mb-3">
                                        <div>
                                            <label className="block text-xs font-medium text-gray-700 mb-1">Start Date</label>
                                            <input
                                                type="date"
                                                value={newPriceDate.startDate || ''}
                                                onChange={(e) => setNewPriceDate({ ...newPriceDate, startDate: e.target.value })}
                                                className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-xs font-medium text-gray-700 mb-1">End Date</label>
                                            <input
                                                type="date"
                                                value={newPriceDate.endDate || ''}
                                                onChange={(e) => setNewPriceDate({ ...newPriceDate, endDate: e.target.value })}
                                                className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                                            />
                                        </div>
                                    </div>
                                    <div className="mb-3">
                                        <label className="block text-xs font-medium text-gray-700 mb-1">Price</label>
                                        <input
                                            type="number"
                                            value={newPriceDate.price || ''}
                                            onChange={(e) => setNewPriceDate({ ...newPriceDate, price: parseFloat(e.target.value) })}
                                            className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                                            placeholder="0.00"
                                            step="0.01"
                                        />
                                    </div>
                                    <div className="flex justify-end space-x-2">
                                        <button
                                            onClick={() => {
                                                setShowPriceDateModal(false);
                                                setNewPriceDate({});
                                            }}
                                            className="px-3 py-1.5 text-sm border border-gray-300 rounded hover:bg-gray-50"
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            onClick={addPriceDate}
                                            className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded hover:bg-blue-700"
                                        >
                                            Add
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};